import {useQuery} from '@tanstack/react-query'
import {InlineRow} from '@/components/shared/InlineRow'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Strong} from '@/components/shared/Strong'
import {Stack} from '@/components/shared/Stack'
import {Combobox, type ComboboxOption} from '@/components/ui/combobox'
import {Skeleton} from '@/components/ui/skeleton'
import {Badge} from '@/components/ui/badge'
import {Label} from '@/components/ui/label'
import type {Party} from '@/types/party'
import {useAppConfig, labelOf} from '@/hooks/use-app-config'
import {useEffect, useState} from 'react'

interface ExistingPartySelectorProps {
    selectedPartyId: string | null
    onSelect: (party: Party | null) => void
    isLoading?: boolean
}

/**
 * Combobox search for existing PERSON parties.
 * On selection, fetches party detail and passes it to the parent.
 */
export function ExistingPartySelector({selectedPartyId, onSelect, isLoading}: ExistingPartySelectorProps) {
    const services = useServices()
    const i18n = useI18n()
    const {partyTypes} = useAppConfig()
    const [searchQuery, setSearchQuery] = useState('')
    const [pickedPartyId, setPickedPartyId] = useState<string | null>(null)

    const {data: searchResults, isLoading: isSearching} = useQuery({
        queryKey: ['parties-search-onboarding', searchQuery],
        queryFn: () => services.parties.list({q: searchQuery, partyType: 'PERSON', size: 10}),
        enabled: searchQuery.length >= 2,
    })

    const {data: selectedParty, isLoading: isFetchingParty} = useQuery({
        queryKey: ['party-detail-onboarding', selectedPartyId],
        queryFn: () => services.parties.detail(selectedPartyId!),
        enabled: !!selectedPartyId,
    })

    const {data: pickedParty} = useQuery({
        queryKey: ['party-detail-onboarding-picked', pickedPartyId],
        queryFn: () => services.parties.detail(pickedPartyId!),
        enabled: !!pickedPartyId && pickedPartyId !== selectedPartyId,
    })

    useEffect(() => {
        if (pickedParty && pickedPartyId && pickedPartyId !== selectedPartyId) {
            onSelect(pickedParty)
        }
    }, [pickedParty, pickedPartyId, selectedPartyId, onSelect])

    const options: ComboboxOption[] = (searchResults?.content ?? []).map(p => ({
        value: String(p.id),
        label: p.derivedDisplayName,
        description: labelOf(partyTypes, p.partyType),
    }))

    const handleSelect = (value: string | null) => {
        if (!value) {
            setPickedPartyId(null)
            onSelect(null)
            return
        }
        setPickedPartyId(value)
    }

    return (
        <Stack gap="sm">
            <div>
                <Combobox
                    options={options}
                    value={selectedPartyId}
                    onValueChange={handleSelect}
                    onSearch={setSearchQuery}
                    placeholder={i18n.onboarding.existingParty.searchPlaceholder}
                    isLoading={isSearching}
                    selectedLabel={selectedParty?.derivedDisplayName}
                    data-testid="existing-party-selector"
                />
            </div>

            {/* Show party details after selection */}
            {selectedPartyId && (isFetchingParty || isLoading) && (
                <Stack gap="sm">
                    <Skeleton className="h-4 w-48"/>
                    <Skeleton className="h-4 w-32"/>
                    <Skeleton className="h-4 w-40"/>
                </Stack>
            )}

            {selectedParty && !isFetchingParty && !isLoading && (
                <div className="rounded-lg border p-4 space-y-2">
                    <InlineRow>
                        <Label className="text-muted-foreground">{i18n.onboarding.person.displayName}:</Label>
                        <Strong>{selectedParty.derivedDisplayName}</Strong>
                    </InlineRow>
                    <InlineRow>
                        <Label className="text-muted-foreground">{i18n.onboarding.existingParty.partyType}:</Label>
                        <Badge variant="outline">{labelOf(partyTypes, selectedParty.partyType)}</Badge>
                    </InlineRow>
                </div>
            )}
        </Stack>
    )
}
